import { apiService } from './ApiService';
import { apiFetch, buildAuthHeaders, buildApiUrl } from './httpClient';

export interface DevisAttachmentItem {
  id: number;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  created_at: string | null;
}

export interface DevisAttachmentList {
  items: DevisAttachmentItem[];
  total: number;
  limit: number;
  offset: number;
}

class DevisAttachmentsApiService {
  private basePath(devisId: string) {
    return `/devis/${devisId}/attachments`;
  }

  getDownloadUrl(devisId: string, attachmentId: number) {
    return `${apiService.getBaseUrl()}${this.basePath(devisId)}/${attachmentId}/download`;
  }

  async list(devisId: string, limit = 50, offset = 0): Promise<DevisAttachmentList> {
    return apiFetch<DevisAttachmentList>(`${this.basePath(devisId)}?limit=${limit}&offset=${offset}`);
  }

  async upload(devisId: string, file: File): Promise<DevisAttachmentItem> {
    const formData = new FormData();
    formData.append('file', file);
    return apiFetch<DevisAttachmentItem>(this.basePath(devisId), {
      method: 'POST',
      body: formData,
    });
  }

  async delete(devisId: string, attachmentId: number): Promise<void> {
    await apiFetch<void>(`${this.basePath(devisId)}/${attachmentId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Télécharge une pièce jointe de manière authentifiée.
   * Utilise fetch avec le header Authorization pour contourner le problème 401
   * lorsqu'on utilise un lien <a href> direct.
   * 
   * @param devisId - ID du devis
   * @param attachmentId - ID de la pièce jointe
   * @param filename - Nom du fichier pour le téléchargement (optionnel)
   */
  async download(devisId: string, attachmentId: number, filename?: string): Promise<void> {
    const path = `${this.basePath(devisId)}/${attachmentId}/download`;
    const url = buildApiUrl(path);
    const headers = buildAuthHeaders();

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      // Récupérer le blob
      const blob = await response.blob();

      // Créer un URL temporaire pour le blob
      const blobUrl = URL.createObjectURL(blob);

      // Créer un lien temporaire et déclencher le téléchargement
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || `attachment-${attachmentId}`;
      document.body.appendChild(link);
      link.click();

      // Nettoyer
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      throw error;
    }
  }
}

export const devisAttachmentsApiService = new DevisAttachmentsApiService();
